import axios from 'axios';
import queryString from 'query-string';
import moment from 'moment-timezone';
import { authXConfig } from '../../router/authx-config';
import { isLocalEnv } from 'common/config';
import { APP_TIMEZONE, DATE_AND_TIME_FORMAT } from 'common/constants';
import { Logger } from 'common/logger';
import wrapper from 'axios-cache-plugin';
import { setupInstanceInterceptors } from 'common/auth/AuthxAPI';

const axiosInst = axios.create({
    validateStatus(status) {
        return status >= 200 && status < 300; // default
    },
});
// Add a response interceptor to handle the double `data` key
axiosInst.interceptors.response.use(
    function(response) {
        // Check if response.data has a `data` key that is an object
        if (response.data && response.data.data && typeof response.data.data === 'object') {
            // Flatten the response structure by replacing response.data with response.data.data
            return { data: response.data.data };
        }
        return response;
    },
    function(error) {
        // Handle response errors
        return Promise.reject(error);
    }
);

axiosInst.interceptors.request.use((config) => {
    const code = localStorage.getItem('auth_code');

    if (code) {
        config.headers = {
            ...config.headers,
            code,
        };
    }

    return config;
});

export const setupReportsApiInterceptors = (dispatch) => {
    setupInstanceInterceptors(axiosInst, dispatch);
};

const http = wrapper(axiosInst, {
    maxCacheSize: 15,
});

// Cache all API requests
http.__addFilter(/.*/);
export const ReportsAPI = http;

export const configureReportsAPI = (token) => {
    Logger.debug('Configuring ReportsAPI, setting token.');
    let bearerToken = `Bearer ${token}`;
    if (isLocalEnv()) {
        bearerToken = '';
    }

    axiosInst.defaults.headers.common.Authorization = bearerToken;
};

export const getStartAndEnd = (selectedDate, aggDate, datasetType, aggregated = false) => {
    // Parse the selected date in the application's timezone (London)
    const date = moment.tz(selectedDate, APP_TIMEZONE).startOf('day');

    let start = moment(date).subtract(1, 'seconds');
    let end = moment(date)
        .add(1, 'days')
        .subtract(1, 'seconds');

    if (aggDate) {
        start = moment(date).startOf('day');
        return {
            start: start.format(DATE_AND_TIME_FORMAT),
        };
    }

    // If it's aggregated, set start and end differently
    if (aggregated || datasetType === 'info' || datasetType === 'restriction') {
        start = moment(date)
            .startOf('day')
            .subtract(1, 'seconds');
        end = moment(date)
            .add(1, 'days')
            .subtract(1, 'seconds');
        return {
            start: start.format(DATE_AND_TIME_FORMAT),
            end: end.format(DATE_AND_TIME_FORMAT),
        };
    }

    // Check if the selected date is in Daylight Saving Time (DST) for London timezone
    const isDST = moment(date)
        .tz(APP_TIMEZONE)
        .isDST(); // Explicitly use London timezone
    // If DST is active, subtract one hour from both start and end times
    if (isDST) {
        start = start.subtract(1, 'hour');
        end = end.subtract(1, 'hour');
    }

    return {
        start: start.format(DATE_AND_TIME_FORMAT),
        end: end.format(DATE_AND_TIME_FORMAT),
    };
};

export const constructQuery = ({
    datasetName: table_id,
    park: site_id,
    date,
    parameters = {},
    aggregated = false,
    aggDate = false,
}) => {
    const { baseURL } = authXConfig.API.ReportsAPI;

    // Construct start and end timestamps if date is provided
    const startAndEnd = date ? getStartAndEnd(date, aggDate, table_id, aggregated) : {};

    // Combine everything into the query string
    const queryParams = {
        ...parameters,
        ...(aggDate ? { aggDate: startAndEnd.start } : startAndEnd),
    };

    // Serialize query params using qs to handle array values correctly
    const queryStr = queryString.stringify(queryParams, {
        arrayFormat: 'repeat', // Ensures `status=S1&status=S2`
    });

    const url = `${baseURL}/query/${site_id}/${table_id}?${queryStr}`;

    Logger.info('Query: ', url);

    return { url };
};

function catchFn(error) {
    if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        const parsedUrl = queryString.parseUrl(error.config.url);
        const dataset = parsedUrl.url.split('/').pop();
        Logger.error(`Status ${error.response.status} in ${dataset}`, {
            query: parsedUrl.query,
            data: error.response.data,
            url: parsedUrl.url,
        });
    } else if (error.request) {
        // The request was made but no response was received
        // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
        // http.ClientRequest in node.js
        Logger.error(error.request);
    } else {
        // Something happened in setting up the request that triggered an Error
        Logger.error('postQuery error', error.message);
    }

    throw error;
}

export const postQuery = ({ url, data }) => {
    // Used to test if correct URL is generated by constructQuery
    if (isLocalEnv()) {
        return url;
    }
    return ReportsAPI.post(url, data).catch(catchFn);
};

export const getQuery = ({ url, params }) => {
    // Used to test if correct URL is generated by constructQuery
    if (isLocalEnv()) {
        return url;
    }
    return ReportsAPI.get(url, { params }).catch(catchFn);
};

const MAX_LIMIT = 1000;

export const park1hQuery = (date, park) =>
    getQuery(
        constructQuery({
            datasetName: 'v$park_1h',
            park,
            date,
        })
    );

export const turbine1dQuery = (date, park, turbine) =>
    getQuery(
        constructQuery({
            datasetName: 'v$turbine_1d',
            park,
            date,
            parameters: { turbine_id: turbine },
            aggregated: true,
        })
    );

export const turbine1dUnavailabliltyQuery = (date, park, turbine) =>
    getQuery(
        constructQuery({
            datasetName: 'turbine_1d_unavailability',
            park,
            date,
            parameters: { turbine_id: turbine },
            aggregated: true,
        })
    );

export const turbine1hQuery = (date, park, turbine) =>
    getQuery(
        constructQuery({
            datasetName: 'v$turbine_1h',
            park,
            date,
            parameters: { turbine_id: turbine },
        })
    );

export const turbine30davgQuery = (date, park, turbine) =>
    getQuery(
        constructQuery({
            datasetName: 'v$turbine_30davg',
            park,
            date,
            parameters: { turbine_id: turbine },
            aggregated: true,
            aggDate: true,
        })
    );

export const park30davgQuery = (date, park) =>
    getQuery(
        constructQuery({
            datasetName: 'v$park_30davg',
            park,
            date,
            aggregated: true,
            aggDate: true,
        })
    );

export const park1dQuery = (date, park) =>
    getQuery(
        constructQuery({
            datasetName: 'v$park_1d',
            park,
            date,
            aggregated: true,
        })
    );

export const releaseQuery = (date, park, turbine) =>
    getQuery(
        constructQuery({
            datasetName: 'rop',
            park,
            date,
            parameters: { turbine_id: turbine },
        })
    );

export const visitorLogQuery = (date, park, turbine) =>
    getQuery(
        constructQuery({
            datasetName: 'visitor_log',
            park,
            date,
            parameters: { turbine_id: turbine },
        })
    );

export const noteQuery = (date, park, selectedTurbineKey) =>
    getQuery(
        constructQuery({
            datasetName: 'info',
            park,
            date,
            parameters: selectedTurbineKey ? { turbine_id: selectedTurbineKey } : undefined,
        })
    );

export const restrictionQuery = (date, park, turbine) =>
    getQuery(
        constructQuery({
            datasetName: 'restriction',
            park,
            date,
            options: { limit: MAX_LIMIT },
            parameters: { turbine_id: turbine },
        })
    );

export const turbineStateQuery = (date, park, turbine) =>
    getQuery(
        constructQuery({
            datasetName: 'turbine_state',
            park,
            date,
            parameters: { turbine_id: turbine },
        })
    );

export const workOrderQueryGet = (date, park, turbine) =>
    getQuery(
        constructQuery({
            datasetName: 'work_order',
            park,
            date,
            parameters: { turbine_id: turbine },
        })
    );

export const workOrderQuery = (date, park, turbine, statuses) =>
    getQuery(
        constructQuery({
            datasetName: 'work_order',
            park,
            date,

            // Currently statuses value doed not matter, if there is any value it will return items with ['COMPLETED', 'ISSUED']
            parameters: { turbine_id: turbine, status: statuses || [] },
        })
    );

export const turbineCurrentStateQuery = (date, park, turbine) =>
    getQuery(
        constructQuery({
            datasetName: 'turbine',
            park,
            date, // This will be today's date in app timezone
            parameters: { turbine_id: turbine },
        })
    );

export const personQuery = (date, park) =>
    getQuery(
        constructQuery({
            datasetName: 'person',
            park,
            date,
        })
    );

export const vesselQuery = (date, park) =>
    getQuery(
        constructQuery({
            datasetName: 'vessel',
            park,
            date,
        })
    );

export const lightningQuery = async (date, park) => {
    try {
        return await getQuery(
            constructQuery({
                datasetName: 'lightning_alert',
                park,
                date,
            })
        );
    } catch (err) {
        Logger.debug('lightning_alert query failed, returning empty dataset', err);
        return { data: { entries: [] } };
    }
};

export const fetchModelOneData = (park, date) =>
    getQuery(
        constructQuery({
            datasetName: `turbines_power_prediction_t4`,
            park,
            date,
        })
    );

export const fetchModelTwoData = (park, date) =>
    getQuery(
        constructQuery({
            datasetName: `turbines_svm_anomaly_t1`,
            park,
            date,
        })
    );

// in axios 0.19 there should be e.isAxiosError,  currently doing manual checking
export const isAxiosError = (e) => e.toString().startsWith('Error: Request failed with status code');

export function* catchErrors(fn, backupFn = null) {
    try {
        yield fn;
    } catch (e) {
        if (!isAxiosError(e)) {
            Logger.error('Catched reportsAPI error', e);
        }

        if (backupFn) yield backupFn;
    }
}

export async function catchErrorsV2(fn, backupFn = null) {
    try {
        await fn(); // Execute the function passed in
    } catch (e) {
        if (!isAxiosError(e)) {
            Logger.error('Caught reportsAPI error', e);
        }

        if (backupFn) backupFn(); // Call backup function if provided
    }
}
